import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Skeleton,
  Fade,
  Zoom,
  Snackbar,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Sync as SyncIcon,
  People as PeopleIcon,
  AssignmentTurnedIn as AssignIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Campaign as CampaignIcon,
  Person as PersonIcon,
  TrendingUp as FunnelIcon,
  Check as SuccessIcon,
  PersonAdd as AddPersonIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  AddCircleOutline as AddCircleIcon,
  Language as WebIcon,
} from '@mui/icons-material';
import { leadService } from '../../services/lead';
import { getAssociates } from '../../services/associate';
import { getMetaIntegrationStatus, updateMetaAssignment } from '../../services/facebook';

// Pure helper — defined outside component so it never changes reference
// Parses GET /api/meta-leads response: { status: true, data: [...] }
const parseIntegrationResponse = (res) => {
  if (!res) return null;
  const list = Array.isArray(res.data) ? res.data
    : Array.isArray(res) ? res
    : null;
  if (!list) return null;
  return list.find((item) => item.status === 'connected' && item.isActive === true) || null;
};

const Funnel = () => {
  // Summary counts and splits states
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [pendingLeadsCount, setPendingLeadsCount] = useState(0);
  const [distributedLeadsCount, setDistributedLeadsCount] = useState(0);
  const [poolSize, setPoolSize] = useState(0);
  const [poolAssociatesObj, setPoolAssociatesObj] = useState(null);
  const [websiteCount, setWebsiteCount] = useState(0);
  const [metaCount, setMetaCount] = useState(0);

  // Pool & Associates state
  const [routingPool, setRoutingPool] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [error, setError] = useState(null);

  // Saved pool IDs (committed on server) and Draft pool IDs (local edits)
  const [savedPoolIds, setSavedPoolIds] = useState([]);
  const [selectedDistributionIds, setSelectedDistributionIds] = useState([]);

  // Interactive UI states
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingPool, setIsSavingPool] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isMetaActive, setIsMetaActive] = useState(false);

  // Associate search for the Add Routing Associate panel
  const [associateSearch, setAssociateSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingAssociates, setIsSearchingAssociates] = useState(false);
  const associateSearchTimerRef = useRef(null);

  // Pagination states for tables
  const [assignedPage, setAssignedPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  
  // Feedback states
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Fetch Dashboard summary, provider splits, and routing pool from backend
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadingCounts(true);
    
    // Helper to safely extract numeric count even if backend returns {total, assigned, unassigned} object
    const parseCount = (val) => {
      if (val && typeof val === 'object') {
        return val.total || 0;
      }
      return val || 0;
    };

    try {
      const res = await leadService.getLeadRoutingDashboard();
      if (res && res.status && res.data) {
        const { summary = {}, routing = {} } = res.data;
        
        setTotalLeadsCount(parseCount(summary.totalLeadsReceived));
        setPendingLeadsCount(parseCount(summary.pendingAssignment));
        setDistributedLeadsCount(parseCount(summary.distributedSuccessfully));
        
        const poolAssociatesVal = summary.poolAssociates;
        setPoolAssociatesObj(poolAssociatesVal || null);
        setPoolSize(parseCount(poolAssociatesVal));
        
        if (summary.byProvider) {
          setWebsiteCount(parseCount(summary.byProvider.website));
          setMetaCount(parseCount(summary.byProvider.meta));
        }
        
        const pool = routing.pool || [];
        setRoutingPool(pool);
        
        const poolIds = pool.map(item => item.associateId || item._id || item.id).filter(Boolean);
        setSavedPoolIds(poolIds);
        setSelectedDistributionIds(poolIds);
      }
      setError(null);
    } catch (err) {
      console.error('[Funnel Error] Failed to fetch routing dashboard:', err);
      setError('Failed to fetch lead routing status. Please check your backend connections.');
    } finally {
      setLoading(false);
      setLoadingCounts(false);
    }
  }, []);

  const checkMetaStatus = useCallback(async () => {
    try {
      const res = await getMetaIntegrationStatus();
      const activeItem = parseIntegrationResponse(res);
      setIsMetaActive(!!activeItem);
    } catch (e) {
      console.warn('[Funnel] Meta status check failed:', e);
      setIsMetaActive(false);
    }
  }, []);

  // Fetch only unassigned Meta Leads from backend (Noop fallback to satisfy local handlers)
  const fetchUnassignedLeads = useCallback(async (isSilent = false, page = 1) => {
    // Left as no-op since all summary stats and pool elements are loaded from dashboard API
  }, []);

  // Load dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
    checkMetaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling interval for auto-refresh
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchDashboardData();
      }, 10000); // refresh every 10 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, fetchDashboardData]);

  // Debounced server-side associate search
  useEffect(() => {
    if (associateSearchTimerRef.current) clearTimeout(associateSearchTimerRef.current);
    associateSearchTimerRef.current = setTimeout(async () => {
      setIsSearchingAssociates(true);
      try {
        const res = await getAssociates({
          limit: 100,
          page: 1,
          isActive: true,
          ...(associateSearch.trim() ? { search: associateSearch.trim() } : {}),
        });
        if (res && Array.isArray(res.data)) {
          setSearchResults(res.data.map(item => ({
            id: item._id || item.id,
            name: item.name || 'Unknown',
            associateId: item.associateId || '',
            loginId: item.userAccess?.loginId || item.loginId || '',
            phone: item.phone || '',
            designation: item.designation || item.role || '—',
          })));
        }
      } catch (e) {
        console.warn('[Funnel] Associate search failed:', e);
      } finally {
        setIsSearchingAssociates(false);
      }
    }, 350);
    return () => clearTimeout(associateSearchTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associateSearch]);

  // Save the modified routing pool using PATCH /api/lead-routing
  const saveRoutingPool = async (nextIds) => {
    setIsSavingPool(true);
    try {
      await leadService.updateLeadRouting({
        assignmentAssociateIds: nextIds
      });
      showToast('Lead routing pool updated successfully.');
      // Refresh dashboard
      await fetchDashboardData();
    } catch (err) {
      console.error('[Funnel Error] Failed to save routing pool:', err);
      showToast(err.message || 'Failed to save routing pool updates.', 'error');
    } finally {
      setIsSavingPool(false);
    }
  };

  // Draft: add associate locally
  const handleAddAssociate = (associateId) => {
    if (selectedDistributionIds.includes(associateId)) return;
    setSelectedDistributionIds(prev => [...prev, associateId]);
  };

  // Draft: remove associate locally
  const handleRemoveAssociate = (associateId) => {
    setSelectedDistributionIds(prev => prev.filter(id => id !== associateId));
  };

  // Submit: save draft to API
  const handleSubmitPool = async () => {
    await saveRoutingPool(selectedDistributionIds);
  };

  // Discard: reset draft back to last saved state
  const handleDiscardChanges = () => {
    setSelectedDistributionIds(savedPoolIds);
  };

  // Detect whether there are unsaved changes
  const hasPoolChanges =
    selectedDistributionIds.length !== savedPoolIds.length ||
    selectedDistributionIds.some(id => !savedPoolIds.includes(id)) ||
    savedPoolIds.some(id => !selectedDistributionIds.includes(id));

  // Force full reload
  const handleForceReload = () => {
    fetchDashboardData();
  };

  // Helper/map to get associate details by ID
  const associateDetailsMap = useMemo(() => {
    const map = new Map();
    // 1. Initial associates from search results
    searchResults.forEach(a => map.set(a.id, a));
    // 2. Already saved pool items from dashboard response
    routingPool.forEach(item => {
      map.set(item.associateId, {
        id: item.associateId,
        name: item.name || 'Unknown',
        loginId: item.loginId || '',
        phone: item.phone || '',
        designation: item.designation || '—',
        assignedCount: item.assignedCount || 0,
        lastAssignedAt: item.lastAssignedAt || null,
      });
    });
    return map;
  }, [searchResults, routingPool]);

  // Combined full details for all selected/assigned associates
  const assignedAssociates = useMemo(() => {
    return selectedDistributionIds
      .map(id => associateDetailsMap.get(id))
      .filter(Boolean);
  }, [selectedDistributionIds, associateDetailsMap]);

  // Paginate assigned associates locally (10 per page)
  const assignedTotalPages = Math.ceil(assignedAssociates.length / 10) || 1;
  const paginatedAssigned = useMemo(() => {
    const start = (assignedPage - 1) * 10;
    return assignedAssociates.slice(start, start + 10);
  }, [assignedAssociates, assignedPage]);

  // Filter out associates that are already in the routing pool from the search results
  const filteredSearchResults = searchResults.filter(
    assoc => !selectedDistributionIds.includes(assoc.id)
  );

  // Paginate search results locally (10 per page by default)
  const searchTotalPages = Math.ceil(filteredSearchResults.length / 10) || 1;
  const paginatedSearchResults = useMemo(() => {
    const start = (searchPage - 1) * 10;
    return filteredSearchResults.slice(start, start + 10);
  }, [filteredSearchResults, searchPage]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Title Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <FunnelIcon sx={{ color: '#536DFE', fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
              Funnel Leads
            </Typography>
            {/* {isMetaActive ? (
              <Chip 
                icon={<FacebookIcon sx={{ '&&': { color: 'white' } }} />} 
                // label={metaPageName ? `Connected: ${metaPageName}` : 'Meta Integration Active'} 
                sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 600, fontSize: '0.75rem', height: '24px' }} 
              />
            ) : (
              <Chip 
                icon={<WarningIcon sx={{ '&&': { color: 'white', fontSize: '14px' } }} />} 
                label="Meta Integration Inactive" 
                sx={{ bgcolor: '#f59e0b', color: 'white', fontWeight: 600, fontSize: '0.75rem', height: '24px' }} 
              />
            )} */}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Incoming Meta Lead distribution, real-time sync, and associate priority routing
          </Typography>
        </Box>

        {/* <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Tooltip title="Polled lead stream update toggle">
            <Button
              variant={autoRefresh ? "contained" : "outlined"}
              onClick={() => {
                setAutoRefresh(!autoRefresh);
                showToast(`Auto refresh turned ${!autoRefresh ? 'ON' : 'OFF'}`);
              }}
              color="primary"
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
            >
              {autoRefresh ? 'Auto Sync: On' : 'Auto Sync: Off'}
            </Button>
          </Tooltip>

          <IconButton 
            onClick={handleForceReload} 
            disabled={isRefreshing || loading || loadingCounts}
            sx={{ 
              bgcolor: 'white', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <SyncIcon />
          </IconButton>
        </Box> */}
      </Box>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>{error}</Alert>
        </Fade>
      )}

      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.015)', border: '1px solid #e2e8f0', borderLeft: '4px solid #536DFE' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Leads Received
              </Typography>
              <Typography variant="h3" fontWeight={800} color="#1e293b">
                {loadingCounts ? <Skeleton width="60px" /> : totalLeadsCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.015)', border: '1px solid #e2e8f0', borderLeft: '4px solid #f59e0b' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Pending Assignment
              </Typography>
              <Typography variant="h3" fontWeight={800} color="#d97706">
                {loadingCounts ? <Skeleton width="60px" /> : pendingLeadsCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.015)', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Distributed Successfully
              </Typography>
              <Typography variant="h3" fontWeight={800} color="#059669">
                {loadingCounts ? <Skeleton width="60px" /> : distributedLeadsCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.015)', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Associate Lead Receivers
              </Typography>
              <Typography variant="h3" fontWeight={800} color="#7c3aed">
                {loading ? <Skeleton width="60px" /> : poolSize}
              </Typography>
              {poolAssociatesObj && typeof poolAssociatesObj === 'object' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                  Assigned: {poolAssociatesObj.assigned || 0} • Unassigned: {poolAssociatesObj.unassigned || 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Provider Splits Row */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', p: 3, gap: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.015)' }}>
            <Box sx={{ p: 2, borderRadius: 3, bgcolor: '#e0f2fe', color: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WebIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                Website Provider Leads
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#0284c7">
                {loadingCounts ? <Skeleton width="60px" /> : websiteCount}
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', p: 3, gap: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.015)' }}>
            <Box sx={{ p: 2, borderRadius: 3, bgcolor: '#eef2ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FacebookIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                Meta Provider Leads
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#3b82f6">
                {loadingCounts ? <Skeleton width="60px" /> : metaCount}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Warning if no Meta integration is active */}
      {!isMetaActive && !loading && (
        <Alert severity="warning" sx={{ mb: 4, borderRadius: 3, p: 2 }}>
          <Typography variant="body1" fontWeight={700} color="warning.dark" sx={{ mb: 0.5 }}>
            Meta Integration is Inactive
          </Typography>
          No active Meta/Facebook integration detected. Please configure and connect Facebook Lead Ads on the Meta Integration settings page first to begin lead distribution.
        </Alert>
      )}

      {/* Main Content Layout — two full-width tables */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

        {/* ── Table 1: Assigned Associates (Routing Pool) ── */}
        <Card sx={{ borderRadius: 4, border: '1px solid #e0e7ff', boxShadow: '0 4px 24px rgba(83,109,254,0.06)', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%)', borderBottom: '1px solid #e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: '#536DFE' }} />
              <Typography variant="subtitle1" fontWeight={800} color="#3730a3">
                Active Lead Receivers
              </Typography>
              <Chip label={`${selectedDistributionIds.length} active`} color="primary" size="small" sx={{ fontWeight: 700, ml: 1 }} />
            </Box>

            {/* Submit / Discard — visible only when there are unsaved changes */}
            <Fade in={hasPoolChanges}>
              <Box sx={{ display: hasPoolChanges ? 'flex' : 'none', gap: 1.5, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Unsaved changes</Typography>
                <Button size="small" variant="outlined" color="inherit" onClick={handleDiscardChanges} disabled={isSavingPool}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                  Discard
                </Button>
                <Button size="small" variant="contained" color="primary" onClick={handleSubmitPool}
                  disabled={isSavingPool}
                  startIcon={isSavingPool ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
                  {isSavingPool ? 'Saving…' : 'Submit'}
                </Button>
              </Box>
            </Fade>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: 44 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Login ID</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Designation</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned Leads</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Assigned</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: 90 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [1, 2, 3].map(i => (
                    <TableRow key={i}>
                      {[1,2,3,4,5,6,7,8].map(c => (
                        <TableCell key={c}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : assignedAssociates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      <PeopleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1, display: 'block', mx: 'auto' }} />
                      No associates in routing pool. Add them from the list below.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAssigned.map((assoc, idx) => {
                    const isNewlyAdded = !savedPoolIds.includes(assoc.id);
                    return (
                      <TableRow
                        key={assoc.id}
                        sx={{
                          bgcolor: isNewlyAdded ? 'rgba(16,185,129,0.04)' : 'white',
                          borderLeft: isNewlyAdded ? '3px solid #34d399' : '3px solid transparent',
                          '&:hover': { bgcolor: isNewlyAdded ? 'rgba(16,185,129,0.07)' : '#f8fafc' },
                        }}
                      >
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem' }}>{(assignedPage - 1) * 10 + idx + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 800,
                              bgcolor: `hsl(${(assoc.name.charCodeAt(0) * 47) % 360}, 60%, 88%)`,
                              color: `hsl(${(assoc.name.charCodeAt(0) * 47) % 360}, 50%, 30%)` }}>
                              {assoc.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color="#1e293b">{assoc.name}</Typography>
                              {isNewlyAdded && <Chip label="Newly Added" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#475569" fontWeight={600}>{assoc.loginId || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                            <Typography variant="body2" color="#475569">{assoc.phone || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={assoc.designation || '—'} size="small" variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem', borderColor: '#e2e8f0', color: '#64748b' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#475569', fontWeight: 700, fontSize: '0.8rem' }}>
                          {assoc.assignedCount ?? 0}
                        </TableCell>
                        <TableCell sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                          {assoc.lastAssignedAt ? new Date(assoc.lastAssignedAt).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove from pool">
                            <span>
                              <Button size="small" variant="outlined" color="error"
                                disabled={isSavingPool}
                                onClick={() => handleRemoveAssociate(assoc.id)}
                                sx={{ minWidth: 0, px: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '0.72rem', textTransform: 'none' }}>
                                Remove
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {assignedTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Pagination
                count={assignedTotalPages}
                page={assignedPage}
                onChange={(e, p) => setAssignedPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Card>

        {/* ── Table 2: Unassigned Associates (search-driven) ── */}
        <Card sx={{ borderRadius: 4, border: '2px solid #e0e7ff', boxShadow: '0 4px 24px rgba(83,109,254,0.07)', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%)', borderBottom: '1px solid #e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddPersonIcon sx={{ color: '#536DFE' }} />
              <Typography variant="subtitle1" fontWeight={800} color="#3730a3">
                Add Associate
              </Typography>
            </Box>
            {/* Search bar inline in header */}
            <TextField
              size="small"
              placeholder="Search by name…"
              value={associateSearch}
              onChange={(e) => {
                setAssociateSearch(e.target.value);
                setSearchPage(1);
              }}
              disabled={isSavingPool}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isSearchingAssociates
                      ? <CircularProgress size={16} sx={{ color: '#536DFE' }} />
                      : <SearchIcon sx={{ color: '#536DFE', fontSize: 20 }} />}
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0 1px 4px rgba(83,109,254,0.1)',
                  '& fieldset': { borderColor: '#c7d2fe' },
                  '&:hover fieldset': { borderColor: '#536DFE' },
                  '&.Mui-focused fieldset': { borderColor: '#536DFE', borderWidth: 2 },
                },
              }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: 44 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Login ID</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Designation</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: 90 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isSearchingAssociates ? (
                  [1, 2, 3].map(i => (
                    <TableRow key={i}>
                      {[1,2,3,4,5,6].map(c => (
                        <TableCell key={c}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredSearchResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      <SearchIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1, display: 'block', mx: 'auto' }} />
                      {associateSearch.trim() ? 'No associates found for this search' : 'Start typing to search associates'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSearchResults.map((assoc, idx) => {
                    const isInPool = selectedDistributionIds.includes(assoc.id);
                    const isSaved = savedPoolIds.includes(assoc.id);
                    return (
                      <TableRow
                        key={assoc.id}
                        sx={{
                          bgcolor: isInPool
                            ? (isSaved ? 'rgba(99,102,241,0.04)' : 'rgba(16,185,129,0.04)')
                            : 'white',
                          borderLeft: isInPool
                            ? (isSaved ? '3px solid #818cf8' : '3px solid #34d399')
                            : '3px solid transparent',
                          '&:hover': { bgcolor: isInPool ? undefined : '#f8fafc' },
                        }}
                      >
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem' }}>{(searchPage - 1) * 10 + idx + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 800,
                              bgcolor: isInPool ? (isSaved ? '#e0e7ff' : '#dcfce7') : `hsl(${(assoc.name.charCodeAt(0) * 47) % 360}, 60%, 88%)`,
                              color: isInPool ? (isSaved ? '#4338ca' : '#15803d') : `hsl(${(assoc.name.charCodeAt(0) * 47) % 360}, 50%, 30%)` }}>
                              {assoc.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={700} color="#1e293b">{assoc.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#475569" fontWeight={600}>{assoc.loginId || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                            <Typography variant="body2" color="#475569">{assoc.phone || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={assoc.designation || '—'} size="small" variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem', borderColor: '#e2e8f0', color: '#64748b' }} />
                        </TableCell>
                        <TableCell>
                          {isInPool ? (
                            <Chip
                              label={isSaved ? 'In Pool' : 'Added'}
                              size="small"
                              color={isSaved ? 'primary' : 'success'}
                              variant={isSaved ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                            />
                          ) : (
                            <Tooltip title="Add to routing pool">
                              <span>
                                <Button size="small" variant="contained" color="primary"
                                  disabled={isSavingPool}
                                  startIcon={<AddCircleIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => { handleAddAssociate(assoc.id); setAssociateSearch(''); }}
                                  sx={{ minWidth: 0, px: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '0.72rem', textTransform: 'none', boxShadow: 'none' }}>
                                  Add
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {searchTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Pagination
                count={searchTotalPages}
                page={searchPage}
                onChange={(e, p) => setSearchPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Card>
      </Box>


      {/* Snackbar notification alert toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setToast(prev => ({ ...prev, open: false }))} 
          severity={toast.severity} 
          icon={<SuccessIcon />}
          sx={{ borderRadius: 3, fontWeight: 700 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Funnel;
